// Coordinates and external map links are shared with the POI sheet — one
// implementation, re-exported so every existing import here keeps working.
export { formatCoords, mapLinkFor, streetViewUrl, drivingDirectionsUrl } from '../../utils/helpers/poiLinks'

import { i18n } from '../../i18n'

const t = (key: string, values?: Record<string, unknown>): string =>
  values ? i18n.global.t(key, values) : i18n.global.t(key)

// Pure helpers for the peak bagging web pages. Everything deterministic lives
// here (not in the Vue components) so it can be unit tested — see
// __tests__/helpers.test.ts.

export interface ListPeak {
  id: string
  dobihNumber?: number
  name: string
  latitude: number | null
  longitude: number | null
  heightM: number | null
  prominenceM?: number | null
  county?: string | null
  bagged: boolean | null
  baggedPeak?: {
    firstBaggedAt: string
    source: string
    verified?: boolean | null
    recording?: { id: string, title?: string | null } | null
  } | null
  // Detail-panel fields. All optional: the list query asks for them, but they
  // are nullable on the wire and most peaks carry none of them.
  description?: string | null
  website?: string | null
  wikipedia?: string | null
  elevationSource?: string | null
  altNames?: string[] | null
}

export interface PeaksPage {
  peaks: ListPeak[]
  hasNextPage: boolean
}

// 0..100, one decimal place. A list with no peaks reads as 0, not NaN.
export function progressPercent (bagged: number, total: number): number {
  if (!total || total <= 0) return 0
  const pct = (Math.max(bagged, 0) / total) * 100
  return Math.round(Math.min(pct, 100) * 10) / 10
}

export function progressLabel (bagged: number, total: number): string {
  return t('peak_bagging.helpers.progress_label', { bagged: Math.max(bagged, 0), total: Math.max(total, 0) })
}

export function formatHeight (heightM: number | null | undefined): string {
  if (heightM === null || heightM === undefined || Number.isNaN(heightM)) return '—'
  return `${Math.round(heightM).toLocaleString('en-GB')} m`
}

// '2024-03-12T10:00:00Z' → '12 Mar 2024'. Anything unparseable renders empty
// rather than 'Invalid Date'.
export function formatBaggedDate (iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Peaks without coordinates (shouldn't happen, but the fields are nullable on
// the wire) are skipped rather than rendered at [0, 0].
export function peaksToGeoJSON (peaks: ListPeak[]): GeoJSON.FeatureCollection {
  const features = peaks.
    filter((peak) => typeof peak.latitude === 'number' && typeof peak.longitude === 'number').
    map((peak) => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [peak.longitude as number, peak.latitude as number] },
      properties: {
        id: peak.id,
        name: peak.name,
        bagged: peak.bagged === true,
        heightM: peak.heightM
      }
    }))
  return { type: 'FeatureCollection', features }
}

// [[minLng, minLat], [maxLng, maxLat]] for fitBounds, or null when no peak has
// coordinates. A single peak yields a degenerate-but-valid bounds box.
export function boundsFromPeaks (peaks: ListPeak[]): [[number, number], [number, number]] | null {
  const coords = peaks.
    filter((peak) => typeof peak.latitude === 'number' && typeof peak.longitude === 'number').
    map((peak) => [peak.longitude as number, peak.latitude as number])
  if (coords.length === 0) return null

  let minLng = coords[0][0]
  let minLat = coords[0][1]
  let maxLng = coords[0][0]
  let maxLat = coords[0][1]
  coords.forEach(([lng, lat]) => {
    minLng = Math.min(minLng, lng)
    minLat = Math.min(minLat, lat)
    maxLng = Math.max(maxLng, lng)
    maxLat = Math.max(maxLat, lat)
  })
  return [[minLng, minLat], [maxLng, maxLat]]
}

export const BAGGED_COLOR = '#fddf00'
export const UNBAGGED_COLOR = '#8b90a0'

// Sprite names, not bundled assets. Both live in the same Mapbox sprite the
// POI layer's `puck_icon_*` markers come from (see BaseMapDataPOIsModule), so
// a peak on this map reads identically to the same peak tapped on a hike map.
export const PEAK_ICON_BAGGED = 'ic_peak_bagged'
export const PEAK_ICON_UNBAGGED = 'ic_peak_unbagged'

/**
 * The marker layer. Picks the bagged/unbagged sprite per feature, so bag state
 * is legible at a glance without a second layer or a paint repaint.
 *
 * `icon-allow-overlap` matches the POI layer: a hill list is dense (282 Munros
 * inside Scotland), and dropping collided markers would make peaks vanish as
 * you zoom out — and an invisible marker is an unclickable one.
 */
export function peakSymbolLayer (sourceId: string): Record<string, unknown> {
  return {
    id: `${sourceId}-markers`,
    type: 'symbol',
    source: sourceId,
    layout: {
      'icon-image': ['case', ['get', 'bagged'], PEAK_ICON_BAGGED, PEAK_ICON_UNBAGGED],
      'icon-size': ['interpolate', ['linear'], ['zoom'], 5, 0.18, 10, 0.25, 14, 0.32],
      'icon-anchor': 'center',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true
    }
  }
}

/**
 * A halo under the selected peak's marker. Filtered to a single id rather than
 * restyled in place, so selecting never rewrites the source data — the marker
 * layer keeps whatever the bag state was.
 */
export function peakSelectionLayer (sourceId: string): Record<string, unknown> {
  return {
    id: `${sourceId}-selected`,
    type: 'circle',
    source: sourceId,
    filter: selectionFilter(null),
    paint: {
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 10, 12, 18],
      'circle-color': BAGGED_COLOR,
      'circle-opacity': 0.25,
      'circle-stroke-width': 2,
      'circle-stroke-color': BAGGED_COLOR
    }
  }
}

/**
 * The filter for the selection layer. A null/empty id matches nothing — the
 * naive `['==', ['get', 'id'], null]` form matches every feature whose id is
 * missing, which on a clean source is none but on a partial one is all of them.
 */
export function selectionFilter (peakId: string | null | undefined): unknown[] {
  if (!peakId) return ['==', ['get', 'id'], '__none__']
  return ['==', ['get', 'id'], peakId]
}

export function peakLabelLayer (sourceId: string): Record<string, unknown> {
  return {
    id: `${sourceId}-labels`,
    type: 'symbol',
    source: sourceId,
    minzoom: 9,
    layout: {
      'text-field': ['get', 'name'],
      'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
      'text-size': 11,
      'text-offset': [0, 1.1],
      'text-anchor': 'top'
    },
    paint: {
      'text-color': '#ffffff',
      'text-halo-color': '#14151b',
      'text-halo-width': 1.2
    }
  }
}

// The self-hosted style JSON every HiiKER map page uses. userId personalises
// style access server-side; anonymous visitors just omit it.
export function mapStyleUrl (origin: string, firebaseUserId?: string | null): string {
  const base = `${origin}/mapbox_style_jsons/standard.json`
  return firebaseUserId ? `${base}?userId=${encodeURIComponent(firebaseUserId)}` : base
}

// Flattens paginated peaksForList responses, deduping by id (a page boundary
// shifting mid-fetch must not double-render a peak).
export function mergePeaksPages (pages: PeaksPage[]): ListPeak[] {
  const seen = new Set<string>()
  const merged: ListPeak[] = []
  pages.forEach((page) => {
    page.peaks.forEach((peak) => {
      if (seen.has(peak.id)) return
      seen.add(peak.id)
      merged.push(peak)
    })
  })
  return merged
}


export function baggedCountOf (peaks: ListPeak[]): number {
  return peaks.filter((peak) => peak.bagged === true).length
}

// The My Peaks dashboard orders by how close each list is to completion (a
// 90%-done Wainwrights round outranks 5-of-2009 Marilyns), name as tiebreak.
// Returns a new array — never mutates the input.
export function sortListsByProgress<T extends { name: string, peaksCount: number, baggedCount?: number | null }> (
  lists: T[]
): T[] {
  return [...lists].sort((a, b) => {
    const diff =
      progressPercent(b.baggedCount || 0, b.peaksCount) - progressPercent(a.baggedCount || 0, a.peaksCount)
    if (diff !== 0) return diff
    return a.name.localeCompare(b.name)
  })
}

export interface BadgedList {
  peaksCount: number
  baggedCount?: number | null
  baggedBadgeUrl?: string | null
  nonBaggedBadgeUrl?: string | null
}

/**
 * Which badge to draw. Coloured once the user is on the board, grey until then
 * — the server ships both so this never needs a round trip when progress
 * changes, and the choice is the viewer's, not the list's.
 */
export function badgeFor (list: BadgedList): string | null {
  const bagged = list.baggedCount || 0

  return (bagged > 0 ? list.baggedBadgeUrl : list.nonBaggedBadgeUrl) || null
}

/** "12 / 204" — the progress form the app uses, kept out of the template. */
export function progressFraction (bagged: number | null | undefined, total: number): string {
  return `${bagged || 0} / ${total || 0}`
}

/**
 * Splits the catalogue into the lists a user has started and the rest.
 *
 * Started lists come from myPeakStats.listProgress, which is ordered by
 * progress and carries the counts; the remainder keeps the server's curated
 * ordering. Matching on slug rather than id because the two come from
 * different queries.
 */
export function partitionByStarted<T extends { slug: string }> (
  all: T[],
  startedSlugs: string[]
): { started: T[], other: T[] } {
  const started = new Set(startedSlugs)

  return {
    started: all.filter(list => started.has(list.slug)),
    other: all.filter(list => !started.has(list.slug))
  }
}

/**
 * A completed list is worth calling out; the app shows the fraction in the
 * list's own colour, and white would read as "not started" next to it.
 */
export function progressColor (list: BadgedList & { highlightColor?: string | null }): string {
  return (list.baggedCount || 0) > 0 ? (list.highlightColor || '#fddf00') : '#8b90a0'
}

/* ── List column ──────────────────────────────────────────────────────────── */

export type PeakFilterMode = 'all' | 'bagged' | 'todo'

/**
 * The left column's search + bag filter.
 *
 * Matching is accent- and case-insensitive over the display name, its alternate
 * names and the county, so "beinn iosail" finds "Beinn Ìosail" and a Welsh
 * speaker searching the Welsh name of a hill listed under its English one still
 * lands on it. Order is preserved — the list is ranked by height upstream and
 * a search must not reshuffle it.
 */
export function filterPeaks (peaks: ListPeak[], query: string, mode: PeakFilterMode = 'all'): ListPeak[] {
  const needle = foldForSearch(query)

  return peaks.filter((peak) => {
    if (mode === 'bagged' && peak.bagged !== true) return false
    if (mode === 'todo' && peak.bagged === true) return false
    if (!needle) return true

    return [peak.name, peak.county, ...(peak.altNames || [])].
      some((value) => foldForSearch(value).includes(needle))
  })
}

/** Lowercase, accent-folded, whitespace-trimmed. Mirrors Peakable.normalize_peak_name's intent. */
export function foldForSearch (value: string | null | undefined): string {
  if (!value) return ''
  return value.toString().normalize('NFD').replace(/\p{Mn}/gu, '').toLowerCase().trim()
}

/**
 * Peak id → its 1-based rank in the full list.
 *
 * The row number has to survive filtering: "#12" means the 12th-highest Munro,
 * not the 12th row of whatever the search happens to show. Built once off the
 * unfiltered list and looked up per row.
 */
export function positionsById (peaks: ListPeak[]): Record<string, number> {
  const positions: Record<string, number> = {}
  peaks.forEach((peak, index) => { positions[peak.id] = index + 1 })
  return positions
}

/* ── Detail column ────────────────────────────────────────────────────────── */

const FEET_PER_METRE = 3.280839895

/** "1,345 m · 4,413 ft" — both units, because hill lists are quoted in both. */
export function formatHeightPair (heightM: number | null | undefined): string {
  if (heightM === null || heightM === undefined || Number.isNaN(heightM)) return '—'
  const feet = Math.round(heightM * FEET_PER_METRE)
  return `${formatHeight(heightM)} · ${feet.toLocaleString('en-GB')} ft`
}



/**
 * The wikipedia column holds either a bare "en:Ben Nevis" title or a full URL.
 * Returns a URL for both, and null for anything it cannot make sense of, so the
 * panel never renders a link to "undefined".
 */
export function wikipediaUrl (value: string | null | undefined): string | null {
  const raw = (value || '').trim()
  if (!raw) return null
  if (/^https?:\/\//i.test(raw)) return raw

  const [lang, ...rest] = raw.split(':')
  if (rest.length > 0 && /^[a-z-]{2,10}$/i.test(lang)) {
    return `https://${lang.toLowerCase()}.wikipedia.org/wiki/${encodeURIComponent(rest.join(':').replace(/ /g, '_'))}`
  }
  return `https://en.wikipedia.org/wiki/${encodeURIComponent(raw.replace(/ /g, '_'))}`
}

/**
 * A website URL fit to render. The amenities table carries a fair amount of
 * junk here — bare "http://" from a truncated OSM tag is the common one — and
 * scheme-less hosts, which need a scheme before they will resolve.
 */
export function websiteUrl (value: string | null | undefined): string | null {
  const raw = (value || '').trim()
  if (!raw || /^https?:\/\/\/?$/i.test(raw)) return null
  if (/^https?:\/\//i.test(raw)) return raw
  if (!/^[\w-]+(\.[\w-]+)+/.test(raw)) return null
  return `https://${raw}`
}

/**
 * How tall the map pane should be so the page fills the viewport under the site
 * chrome, clamped so a short window (or a tall nav) never collapses it to a
 * sliver. Measured rather than hard-coded: the nav's height moves with the
 * breakpoint and with whether a promo bar is showing.
 *
 * `reserve` leaves a strip of the next section showing. Mapbox's scroll handler
 * eats the wheel, so a pane that filled the viewport exactly left the content
 * below it unreachable — there was nowhere to put the cursor that would scroll
 * the page instead of zooming the map.
 */
export function mapPaneHeight (
  viewportHeight: number,
  topOffset: number,
  minHeight = 420,
  reserve = 0
): number {
  const available = Math.round(viewportHeight - Math.max(topOffset, 0) - Math.max(reserve, 0))
  return Math.max(available, minHeight)
}

/**
 * A peak's description as plain text.
 *
 * `Amenity#description` is an ActionText field (`has_rich_text :description` in
 * Poiable), so GraphQL hands back rendered markup — `<div class="trix-content">
 * …</div>` around the body. Interpolating that into the template prints the
 * tags; `v-html`-ing it would put server HTML into the page for no gain, since
 * the panel wants one paragraph of prose. So: parse it inertly and take the
 * text. A plain-text description (most of them) passes through untouched.
 */
export function plainDescription (value: string | null | undefined): string {
  const raw = (value || '').trim()
  if (!raw || !raw.includes('<')) return raw

  // DOMParser is inert — unlike innerHTML, nothing in the parsed document runs
  // or loads, so an <img onerror> smuggled into an OSM tag stays a no-op.
  const text = new DOMParser().parseFromString(raw, 'text/html').body.textContent || ''
  return text.replace(/\s+/g, ' ').trim()
}

/** Width at which the floating side panels become bottom sheets. Matches app.scss. */
export const SHEET_BREAKPOINT_PX = 991

/** Panel geometry, kept in step with app.scss so the map padding matches what is drawn. */
export const PANEL_WIDTH_PX = 320
export const PANEL_INSET_PX = 10
/** Share of the map height a bottom sheet covers below the breakpoint. */
export const SHEET_FRACTION = 0.62

export interface MapPadding { top: number, right: number, bottom: number, left: number }

/**
 * `padding` for fitBounds/flyTo, so a peak lands in the part of the map the
 * user can actually see.
 *
 * The panels float over the canvas rather than sitting beside it, so centring
 * on the canvas centre puts a peak behind whichever panel is open. Padding is
 * how mapbox is told "this edge is covered" — it shifts the centre without
 * changing the zoom maths, which an `offset` in pixels would not do for
 * fitBounds.
 *
 * Clamped to just under half the canvas in each axis: mapbox cannot satisfy
 * padding that leaves no viewport, and silently produces a broken camera when
 * asked to.
 */
export function mapViewPadding (options: {
  viewportWidth: number
  paneWidth: number
  paneHeight: number
  listOpen: boolean
  detailOpen: boolean
}): MapPadding {
  const { viewportWidth, paneWidth, paneHeight, listOpen, detailOpen } = options
  const gutter = PANEL_WIDTH_PX + PANEL_INSET_PX * 2

  const raw = viewportWidth <= SHEET_BREAKPOINT_PX
    ? { top: 0, right: 0, bottom: (listOpen || detailOpen) ? paneHeight * SHEET_FRACTION : 0, left: 0 }
    : {
        top:    PANEL_INSET_PX,
        right:  detailOpen ? gutter : 0,
        bottom: PANEL_INSET_PX,
        left:   listOpen ? gutter : 0
      }

  return {
    top:    clampPadding(raw.top, paneHeight),
    right:  clampPadding(raw.right, paneWidth),
    bottom: clampPadding(raw.bottom, paneHeight),
    left:   clampPadding(raw.left, paneWidth)
  }
}

function clampPadding (value: number, extent: number): number {
  const limit = Math.max(Math.floor(extent / 2) - 1, 0)
  return Math.max(Math.min(Math.round(value), limit), 0)
}

/* ── Hikes that summit a peak ─────────────────────────────────────────────── */

export interface SummitingHike {
  distanceM: number
  /** Peaks this route summits besides the one being viewed. */
  otherPeaksCount: number
  hike: {
    id: string
    name: string
    canonicalUrl: string | null
    distanceMeters: number | null
    elevationGainMeters: number | null
    difficulty: string | null
    averageRating: number | null
    ratingsCount: number | null
    primaryImageUrl: string | null
  }
}

const METRES_PER_MILE = 1609.344

/** Route length in the viewer's units: "152.9 km" / "95.0 mi". */
export function formatRouteDistance (meters: number | null | undefined, metric = true): string {
  if (meters === null || meters === undefined || Number.isNaN(meters)) return '—'
  const value = metric ? meters / 1000 : meters / METRES_PER_MILE
  return `${value.toFixed(1)} ${metric ? 'km' : 'mi'}`
}

/** Total ascent in the viewer's units: "2,692 m" / "8,832 ft". */
export function formatAscent (meters: number | null | undefined, metric = true): string {
  if (meters === null || meters === undefined || Number.isNaN(meters)) return '—'
  const value = metric ? meters : meters * FEET_PER_METRE
  return `${Math.round(value).toLocaleString('en-GB')} ${metric ? 'm' : 'ft'}`
}

/* ── Peak lists a peak belongs to ─────────────────────────────────────────── */

export interface PeakListProgress {
  id: string
  slug: string
  name: string
  peaksCount: number
  baggedCount: number | null
  highlightColor: string | null
}

/**
 * Orders the list cards the way the app does: the ones you are actually working
 * on first, then the rest by size. A peak sits on up to five or six lists
 * (Ben Nevis is a Munro, a Marilyn, a Sim and a HuMP), and without this the
 * order is whatever the join happened to return.
 */
export function sortPeakLists (lists: PeakListProgress[]): PeakListProgress[] {
  return [...lists].sort((a, b) => {
    const started = Number((b.baggedCount || 0) > 0) - Number((a.baggedCount || 0) > 0)
    if (started !== 0) return started

    const progress = progressPercent(b.baggedCount || 0, b.peaksCount) -
                     progressPercent(a.baggedCount || 0, a.peaksCount)
    if (progress !== 0) return progress

    return a.name.localeCompare(b.name)
  })
}

/* ── Hike cards ───────────────────────────────────────────────────────────── */

/**
 * The app colours difficulty rather than just printing it — Easy green through
 * Extra Difficult red — so the grade is readable at a glance down a list.
 * Unknown grades stay muted rather than guessing a colour.
 */
export function difficultyColor (difficulty: string | null | undefined): string {
  switch ((difficulty || '').trim().toLowerCase()) {
    case 'easy': return '#4caf50'
    case 'medium': return '#ff9800'
    case 'difficult': return '#f4511e'
    case 'extra difficult': return '#e53935'
    default: return '#9da0ad'
  }
}

/** "4.2 (16)", or empty when nothing has been rated yet. */
export function formatRating (rating: number | null | undefined, count: number | null | undefined): string {
  if (rating === null || rating === undefined || Number.isNaN(rating) || rating <= 0) return ''
  const reviews = count || 0
  return reviews > 0 ? `${rating.toFixed(1)} (${reviews})` : rating.toFixed(1)
}

/** "Summits 4 other peaks" / "Summits 1 other peak" / nothing at zero. */
export function otherPeaksLabel (count: number | null | undefined): string {
  const n = count || 0
  if (n <= 0) return ''
  return n === 1 ? t('peak_bagging.helpers.summits_one_other_peak') : t('peak_bagging.helpers.summits_count_other_peaks', { count: n })
}

/* ── External links from the detail header ────────────────────────────────── */



/* ── SerpApi reviews ──────────────────────────────────────────────────────── */

export interface SerpReview {
  author: string
  rating: number | null
  text: string
  when: string
}

interface RawSerpReview {
  username?: string
  author_name?: string
  rating?: number
  description?: string
  text?: string
  relative_time_description?: string
  date?: string
}

/**
 * Normalises the two review shapes the proxy can return.
 *
 * SerpApi moved reviews into `user_reviews.most_relevant`, but older cached
 * payloads still carry a flat `reviews` array — the client passes both through
 * untouched, so the panel has to cope with either. Reviews with no text at all
 * are dropped: a bare star rating adds nothing under a list that already shows
 * the average.
 */
export function normaliseSerpReviews (
  place: { user_reviews?: { most_relevant?: RawSerpReview[] }, reviews?: RawSerpReview[] } | null | undefined,
  limit = 3
): SerpReview[] {
  const raw = place?.user_reviews?.most_relevant || place?.reviews || []

  return raw.
    map((review) => ({
      author: (review.username || review.author_name || t('peak_bagging.helpers.anonymous')).trim(),
      rating: typeof review.rating === 'number' ? review.rating : null,
      text: (review.description || review.text || '').trim(),
      when: (review.relative_time_description || review.date || '').trim()
    })).
    filter((review) => review.text.length > 0).
    slice(0, Math.max(limit, 0))
}

/* ── List catalogue cards ─────────────────────────────────────────────────── */

/**
 * The line under a list card's name.
 *
 * Signed in it is your progress; signed out there is no progress to show, so it
 * falls back to the size of the list — which is the thing a visitor is actually
 * choosing between. Kept out of the template so both pages that render the card
 * cannot drift.
 */
export function listCardSubtitle (
  signedIn: boolean,
  baggedCount: number | null | undefined,
  peaksCount: number
): string {
  if (!signedIn) return t('peak_bagging.helpers.count_peaks', { count: (peaksCount || 0).toLocaleString('en-GB') })
  return progressFraction(baggedCount, peaksCount)
}

/* ── Filtering and sorting a grid of lists ────────────────────────────────── */

export type ListSortMode = 'default' | 'bagged' | 'alpha'
export type ListBagFilter = 'all' | 'bagged' | 'unbagged'

export interface ControllableList {
  name: string
  regionLabel?: string | null
  peaksCount: number
  baggedCount?: number | null
}

export interface ListControls {
  region?: string
  bagState?: ListBagFilter
  sort?: ListSortMode
}

/** The regions present in a set of lists, for the filter dropdown. Sorted, no blanks. */
export function regionOptions (lists: ControllableList[]): string[] {
  const names = lists.map((list) => list.regionLabel).filter((name): name is string => Boolean(name))
  return [...new Set(names)].sort((a, b) => a.localeCompare(b))
}

/**
 * Region filter, bag-state filter and sort, applied in that order.
 *
 * One function rather than three chained in each component: the catalogue and
 * the dashboard both run it, and the order matters — sorting before filtering
 * would be wasted work, and filtering by bag state before region would give the
 * same answer but read as if it did not.
 *
 * 'default' sort preserves the incoming order, which for the catalogue is the
 * server's curated one. Never mutates the input.
 */
export function applyListControls<T extends ControllableList> (lists: T[], controls: ListControls = {}): T[] {
  const { region = '', bagState = 'all', sort = 'default' } = controls

  let out = region ? lists.filter((list) => list.regionLabel === region) : [...lists]

  if (bagState !== 'all') {
    const wantBagged = bagState === 'bagged'
    out = out.filter((list) => ((list.baggedCount || 0) > 0) === wantBagged)
  }

  if (sort === 'alpha') {
    out.sort((a, b) => a.name.localeCompare(b.name))
  } else if (sort === 'bagged') {
    // Absolute count, not percentage — "most bagged" means the most summits,
    // so 200 of 2007 Marilyns outranks 8 of 8 of some small list. Name breaks
    // the tie so a page of untouched lists is not in arbitrary order.
    out.sort((a, b) => ((b.baggedCount || 0) - (a.baggedCount || 0)) || a.name.localeCompare(b.name))
  }

  return out
}

/* ── GPX import review ────────────────────────────────────────────────────── */

export interface ImportSummary {
  recordingsProcessed: number
  newlyBaggedCount: number
  alreadyBaggedCount: number
  newlyBagged: { id: string, name: string, heightM: number | null }[]
  listsTouched: { slug: string, name: string }[]
}

/**
 * The headline for the import review.
 *
 * Says what the import actually achieved rather than that it succeeded: a
 * re-import of hills you have already done is a real and unremarkable outcome,
 * and reading "0 peaks bagged" as a failure would be wrong.
 */
export function importReviewHeadline (summary: ImportSummary): string {
  const { recordingsProcessed: recordings, newlyBaggedCount: bagged, alreadyBaggedCount: already } = summary
  const one = recordings === 1

  if (bagged > 0) {
    if (one) {
      return bagged === 1
        ? t('peak_bagging.helpers.import_one_recording_one_peak')
        : t('peak_bagging.helpers.import_one_recording_count_peaks', { bagged })
    }
    return bagged === 1
      ? t('peak_bagging.helpers.import_count_recordings_one_peak', { recordings })
      : t('peak_bagging.helpers.import_count_recordings_count_peaks', { recordings, bagged })
  }
  if (already > 0) {
    return one
      ? t('peak_bagging.helpers.import_one_recording_already_bagged')
      : t('peak_bagging.helpers.import_count_recordings_already_bagged', { recordings })
  }
  return one
    ? t('peak_bagging.helpers.import_one_recording_no_listed_peaks')
    : t('peak_bagging.helpers.import_count_recordings_no_listed_peaks', { recordings })
}

/** "Munros and Marilyns", "Munros, Marilyns and Simms" — the lists a batch moved. */
export function listNamesSentence (lists: { name: string }[]): string {
  const names = lists.map((list) => list.name).filter(Boolean)
  if (names.length === 0) return ''
  if (names.length === 1) return names[0]
  return t('peak_bagging.helpers.names_and_last', { names: names.slice(0, -1).join(', '), last: names[names.length - 1] })
}


/**
 * The Status and Sort chips on My Peaks. Values are the GraphQL enum members
 * (Types::PeakListStatusEnum / PeakListSortEnum) — kept as literals here so a
 * renamed enum member fails a test rather than silently sending an invalid
 * value the server rejects at query-validation time.
 */
export const PEAK_LIST_STATUS_OPTIONS = [
  { value: '', get label () { return t('peak_bagging.helpers.status') } },
  { value: 'STARTED', get label () { return t('peak_bagging.helpers.started') } },
  { value: 'NOT_STARTED', get label () { return t('peak_bagging.helpers.not_started') } },
  { value: 'COMPLETED', get label () { return t('shared.completed') } }
] as const

export const PEAK_LIST_SORT_OPTIONS = [
  { value: '', get label () { return t('peak_bagging.helpers.sort') } },
  { value: 'POSITION', get label () { return t('peak_bagging.helpers.featured') } },
  { value: 'PROGRESS', get label () { return t('peak_bagging.helpers.progress') } },
  { value: 'NAME', get label () { return t('shared.name') } },
  { value: 'PEAKS_COUNT', get label () { return t('peak_bagging.helpers.size') } }
] as const

export interface PeakListFilterState {
  countrySlug?: string
  status?: string
  sortBy?: string
}

/**
 * Filter state → GraphQL variables. Blank chips are omitted rather than sent
 * as empty strings: `countrySlug: ""` matches no Country and would return
 * nothing, and `status: ""` is not a member of the enum so the whole query
 * would fail validation.
 */
export function peakListFilterVariables (state: PeakListFilterState): Record<string, string> {
  const variables: Record<string, string> = {}
  if (state.countrySlug) variables.countrySlug = state.countrySlug
  if (state.status) variables.status = state.status
  if (state.sortBy) variables.sortBy = state.sortBy
  return variables
}

export interface CountryOption {
  slug: string
  name: string
}

/**
 * The options for the country chip, from `peakListCountries`.
 *
 * Replaces regionOptions. A list links to every country it covers, so a
 * cross-border list (Marilyns, HuMPs) is reachable under each one — the region
 * chip read regionLabel, which is null on exactly those lists, so they could
 * never be filtered to.
 *
 * Taken from the server's whole-catalogue country list rather than gathered
 * from the lists in hand: those are one page of the catalogue, so a country
 * whose lists all sort past page one would never be offered.
 *
 * The slug is what `peakLists(countrySlug:)` matches; the name is what the
 * option reads as. Deduped on slug and sorted by name here too, so the chip
 * stays well-formed whatever order the response arrives in.
 */
export function countryOptions (
  countries: ({ slug?: string | null, name?: string | null } | null)[] | null | undefined
): CountryOption[] {
  const bySlug = new Map<string, CountryOption>()

  for (const country of countries ?? []) {
    const slug = country?.slug
    const name = country?.name
    if (typeof slug !== 'string' || slug.length === 0) continue
    if (typeof name !== 'string' || name.length === 0) continue
    if (!bySlug.has(slug)) bySlug.set(slug, { slug, name })
  }

  return Array.from(bySlug.values()).sort((a, b) => a.name.localeCompare(b.name))
}

/* ── The all-lists map page (/peaks) ──────────────────────────────────────── */

/** One pin as `peakList.mapPoints` returns it. A subset of ListPeak. */
export interface MapPoint {
  id: string
  name: string | null
  latitude: number
  longitude: number
  bagged: boolean
  heightM?: number | null
  county?: string | null
}

export interface MapPointList {
  slug: string
  mapPoints?: MapPoint[] | null
}

/**
 * The peaks of every switched-on list, as one set.
 *
 * Deduped by peak id, which is the whole point: Ben Nevis is a Munro, a
 * Marilyn, a Sim and a HuMP, so switching those lists on without this would put
 * four rows in the column and stack four pins on one summit.
 *
 * `bagged` is OR-ed across the copies. It is the same viewer either way so the
 * copies should agree, but a disagreement should read as bagged rather than
 * losing a tick to whichever row happened to be merged second.
 *
 * Ordered highest first — the order the list column and every peak list is read
 * in — with name breaking ties so the order is stable between renders rather
 * than depending on which list was toggled on first. Peaks with no height sort
 * last rather than to the top, which is where a null would otherwise land.
 */
export function mergeListPoints (lists: MapPointList[], enabledSlugs: string[]): ListPeak[] {
  const enabled = new Set(enabledSlugs)
  const byId = new Map<string, ListPeak>()

  lists.forEach((list) => {
    if (!enabled.has(list.slug)) return

    ;(list.mapPoints || []).forEach((point) => {
      const existing = byId.get(point.id)
      if (existing) {
        if (point.bagged) existing.bagged = true
        return
      }

      byId.set(point.id, {
        id:        point.id,
        name:      point.name || '',
        latitude:  point.latitude,
        longitude: point.longitude,
        heightM:   point.heightM ?? null,
        county:    point.county ?? null,
        bagged:    point.bagged
      })
    })
  })

  return [...byId.values()].sort((a, b) => {
    const heightA = a.heightM ?? -Infinity
    const heightB = b.heightM ?? -Infinity
    return heightB - heightA || a.name.localeCompare(b.name)
  })
}

/**
 * Switch a list on or off, as a new array.
 *
 * Returned rather than mutated so the caller assigns to a ref and Vue sees the
 * change — splicing in place leaves a computed reading the same array identity.
 */
export function toggleSlug (slugs: string[], slug: string): string[] {
  return slugs.includes(slug) ? slugs.filter((entry) => entry !== slug) : [...slugs, slug]
}

/**
 * The page numbers a pager should draw, with `0` standing in for a gap.
 *
 * 500+ lists at 24 a page is 20-odd pages, which is too many to draw as a row
 * of buttons. This keeps the first page, the last page, and a window around
 * the current one, so the control stays a fixed width however deep the
 * catalogue gets.
 */
export function pageWindow (currentPage: number, totalPages: number, span = 1): number[] {
  if (totalPages <= 1) return totalPages === 1 ? [1] : []

  const current = Math.min(Math.max(currentPage, 1), totalPages)
  const wanted = new Set<number>([1, totalPages])
  for (let page = current - span; page <= current + span; page += 1) {
    if (page >= 1 && page <= totalPages) wanted.add(page)
  }

  const pages = [...wanted].sort((a, b) => a - b)
  const out: number[] = []
  pages.forEach((page, index) => {
    if (index > 0 && page - pages[index - 1] > 1) out.push(0)
    out.push(page)
  })
  return out
}

/* ── My peak lists (the viewer's own, on /user/peaks) ─────────────────────── */

export type MyListSort = 'progress' | 'completion' | 'name' | 'size'

export const MY_LIST_SORT_OPTIONS = [
  { value: 'progress', get label () { return t('peak_bagging.helpers.most_bagged') } },
  { value: 'completion', get label () { return t('peak_bagging.helpers.closest_to_done') } },
  { value: 'name', get label () { return t('shared.name') } },
  { value: 'size', get label () { return t('peak_bagging.helpers.size') } }
] as const

export interface MyListRow {
  name: string
  regionLabel?: string | null
  locationLabel?: string | null
  peaksCount: number
  baggedCount?: number | null
}

/**
 * Search and sort for the viewer's own lists.
 *
 * Client-side, unlike the Other grid below it: `myLists` comes back unpaged
 * because nobody is on 500 lists of their own, so the rows are already here and
 * narrowing them should not cost a round trip.
 *
 * 'progress' is the raw count and 'completion' the fraction — they are
 * different questions ("where have I done the most?" against "what could I
 * finish?"), and collapsing them into one sort was what made the old single
 * "progress" option read wrongly on a big list barely started.
 */
export function applyMyListControls<T extends MyListRow> (
  lists: T[],
  controls: { query?: string, sort?: MyListSort } = {}
): T[] {
  const { query = '', sort = 'progress' } = controls
  const needle = foldForSearch(query)

  const matched = needle
    ? lists.filter((list) => [list.name, list.regionLabel, list.locationLabel].
      some((value) => foldForSearch(value).includes(needle)))
    : [...lists]

  return matched.sort((a, b) => {
    switch (sort) {
      case 'name': return a.name.localeCompare(b.name)
      case 'size': return (b.peaksCount || 0) - (a.peaksCount || 0) || a.name.localeCompare(b.name)
      case 'completion':
        return progressPercent(b.baggedCount || 0, b.peaksCount) -
               progressPercent(a.baggedCount || 0, a.peaksCount) ||
               a.name.localeCompare(b.name)
      default:
        return (b.baggedCount || 0) - (a.baggedCount || 0) || a.name.localeCompare(b.name)
    }
  })
}

/* ── List selection, filtering and linking ────────────────────────────────── */

export type PeakListStatus = 'not_started' | 'started' | 'completed'

export interface ListCountry {
  slug: string
  name: string
}

export interface FilterableList {
  slug: string
  name?: string | null
  regionLabel?: string | null
  peaksCount: number
  baggedCount?: number | null
  countries?: (ListCountry | null)[] | null
}

/**
 * Which lists the map should plot.
 *
 * Nothing ticked means everything, because the map opens on every peak and
 * "Clear selection" puts it back there — the two states have to mean the same
 * thing or clearing would empty the map.
 *
 * A selection is filtered against the lists that actually exist: a slug left
 * over from a list that has since gone would otherwise blank the map with no
 * way for the reader to see why. If that leaves nothing at all, it falls back
 * to everything rather than showing an empty map.
 */
export function shownSlugs<T extends { slug: string }> (lists: T[], selected: string[]): string[] {
  const known = new Set(lists.map((list) => list.slug))
  const matched = selected.filter((slug) => known.has(slug))

  return matched.length > 0 ? matched : lists.map((list) => list.slug)
}

/**
 * Where a viewer stands on one list.
 *
 * Over-full counts as completed: bagging can outrun the list's own count
 * between two queries, and reading that as "started" would take a finished
 * round off the completed filter.
 *
 * An empty list is never completed — 0 of 0 is not a round.
 */
export function listStatus (list: { peaksCount: number, baggedCount?: number | null }): PeakListStatus {
  const total = list.peaksCount || 0
  const bagged = list.baggedCount || 0

  if (bagged <= 0) return 'not_started'
  if (total > 0 && bagged >= total) return 'completed'
  return 'started'
}

/**
 * Search, status and country, applied together.
 *
 * The search covers the list name, its region label and every country it
 * covers, accent- and case-folded on both sides — "eire" has to find "Éire",
 * because nobody types the accent into a search box.
 *
 * A country matches if the list covers it at all. That is the whole reason the
 * association is many-to-many: a list spanning four countries has to appear
 * under each of them, and a list filed under none cannot match a country
 * filter — but must not throw either.
 *
 * Order is preserved. The server curates it; a search narrows that order, it
 * does not resort it.
 */
export function filterLists<T extends FilterableList> (
  lists: T[],
  query: string,
  status: string,
  countrySlug = ''
): T[] {
  const needle = foldForSearch(query)

  return lists.filter((list) => {
    const countries = (list.countries || []).filter(Boolean) as ListCountry[]

    if (countrySlug && !countries.some((country) => country.slug === countrySlug)) return false
    if (status && status !== 'all' && listStatus(list) !== status) return false
    if (!needle) return true

    return [list.name, list.regionLabel, ...countries.map((country) => country.name)].
      some((value) => foldForSearch(value).includes(needle))
  })
}

/** The countries a set of lists covers, deduped on slug and A-Z by name. */
export function countryOptionsForLists (lists: FilterableList[]): ListCountry[] {
  const bySlug = new Map<string, ListCountry>()

  lists.forEach((list) => {
    (list.countries || []).forEach((country) => {
      if (!country?.slug || bySlug.has(country.slug)) return
      bySlug.set(country.slug, country)
    })
  })

  return [...bySlug.values()].sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Where a list card links.
 *
 * The server builds the path because it carries the country the list is filed
 * under, which the client cannot derive. The fallback is the pre-country URL,
 * which 301s to the canonical one — so a component whose query has not been
 * updated to ask for `webPath` still links somewhere real.
 */
export function peakListPath (list: { slug: string, webPath?: string | null }): string {
  return list.webPath || `/peaks/lists/${list.slug}`
}
